/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2023 (c) Daniel Littfinski, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2024 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2026 (c) Nico Brandt, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import NewOpcUaClient from './NewOpcUaClient';
import OpcUaClientFunc from './OpcUaClient';
import RmCompany from './RmCompany';
import { Company, OpcUaClient, UserClient } from 'Api/generated';
import React, { useEffect, useState } from 'react';
import Api from 'Api/Api';
import { AllowedRoles, useAuth } from 'Contexts/Auth';

interface ICompanyDetailsProps {
  companies: Company[];
  company: Company;
  onDelete: () => void;
}

export default function CompanyDetails({ company, onDelete, companies }: ICompanyDetailsProps) {
  const [errorStr, setErrorStr] = useState<string | undefined>();
  const [opcUaClients, setOpcUaClients] = useState<OpcUaClient[] | null>(null);
  const [clientsChange, setClientsChange] = useState<number>(0);
  const [availableUsers, setAvailableUsers] = useState<UserClient[]>([]);
  const auth = useAuth();
  const isAdminOrSuperAdmin = auth.user?.Roles?.some((r) => r === 'SuperAdmin' || r === 'Admin') ?? false;

  useEffect(() => {
    setOpcUaClients(null);
    setErrorStr(undefined);
    Api.OpcUaClient.getClientsOfCompany(company.Id || '')
      .then((data) => setOpcUaClients(data.data))
      .then(() => setErrorStr(undefined))
      .catch((e) => setErrorStr(String(e)));
  }, [company.Id, clientsChange]);

  useEffect(() => {
    if (isAdminOrSuperAdmin) {
      Api.Users.getUsers()
        .then((data) => setAvailableUsers(data.data))
        .catch((e) => setErrorStr(String(e)));
    }
  }, [isAdminOrSuperAdmin]);

  if (errorStr !== undefined) {
    return (
      <div className="alert alert-danger" role="alert">
        {errorStr}
      </div>
    );
  }
  if (opcUaClients === null) {
    return <span>Loading Clients...</span>;
  }

  const onChange = function () {
    setClientsChange(clientsChange + 1);
  };

  const onDeleteCompany = function () {
    onDelete();
  };

  const CompanyHead = (
    <RmCompany company={company} companyList={companies} onChange={onChange} onDeletedCompany={onDeleteCompany} />
  );

  const CompanyClients = opcUaClients.map((client) => (
    <OpcUaClientFunc
      client={client}
      company={company}
      clients={opcUaClients}
      key={client.Id}
      onChange={onChange}
      availableUsers={availableUsers}
    />
  ));

  const NewClientForm = <NewOpcUaClient companyId={company.Id} clients={opcUaClients} onChange={onChange} />;

  return (
    <div>
      {CompanyHead}
      <div className="row border-bottom pb-2 mb-2"></div>
      {CompanyClients}
      <AllowedRoles roles={['SuperAdmin', 'Admin']}>{NewClientForm}</AllowedRoles>
    </div>
  );
}
